import { format } from 'date-fns'

/**
 * Clean-up the url by replacing '//' by single '/'
 * @param {*} url
 * @returns
 */
function cleanUrl(url: string) {
  return url.replace(/\/{2,}/g, '/')
}

/**
 * String formatting with multiple parameters
 * See https://stackoverflow.com/questions/7790811/how-do-i-put-variables-inside-javascript-strings
 * @param {*} str
 * @param {*} arr the parameters to replace into the str string parameters
 * @returns ex.: dformat("uid:%1, name:%2", 120, "someone") => uid:120, name:someone
 */
function dformat(str: string, ...arr) {
  return str.replace(/%(\d+)/g, function (_, i) {
    return arr[--i]
  })
}

/**
 * Reverse function of <convertDatePformatToArrayOfNumber>
 * Flatten a date stores as a number[yyyy, mm, dd] to a LocalDate for the backend endpoints requiring it
 * @param { } aDate
 * @returns
 */
function convertArrayDateToPformat(aDate: number[]) {
  let aDateFlattened = ''
  if (aDate && aDate.length === 3) {
    aDateFlattened = format(new Date(aDate[0], aDate[1] - 1, aDate[2], 23, 59), 'P')
  }
  return aDateFlattened
}

/**
 * Reverse function of <convertArrayDateToPformat>
 * Transform a date from P format, to number[yyyy, mm, dd]
 * @param { } aDate in string P-format
 * @returns [yyyy, mm, jj]
 */
function convertDatePformatToArrayOfNumber(aDate: string) {
  return [parseInt(aDate?.substring(6)), parseInt(aDate?.substring(3, 5)), parseInt(aDate?.substring(0, 4))]
}

/**
 * Convert a date from P-format (01/01/2025) to format YYYYMMJJ (20250101)
 * @param {*} aDate
 * @returns
 */
function convertDatePformatToYyyyMmJj(aDate: string) {
  return aDate?.substring(6) + aDate?.substring(3, 5) + aDate?.substring(0, 2)
}

/**
 * Convert a date from P-format (01/01/2025) to format YYYY<SEP>MM<SEP>JJ (ex with SEP='-' => 2025-01-01)
 * @param {*} aDate
 * @returns
 */
function convertDatePformatToYyyySepMmSepJj(aDate: string, sep: string) {
  return aDate?.substring(6) + sep + aDate?.substring(3, 5) + sep + aDate?.substring(0, 2)
}

/**
 * Converst a date from YYYYMMJJ (20250101) format to P-format (01/01/2025)
 * @param {*} aDate
 * @returns
 */
function convertDateYyyyMmJjToPformat(aDate: string) {
  return aDate?.substring(6) + '/' + aDate?.substring(4, 6) + '/' + aDate?.substring(0, 4)
}

/**
 * Convert to LocalDate a date stored as a number[yyyy, mm, dd]
 * @param { } aDate
 * @returns
 */
function convertArrayDateToLocalDate(aDate: number[]) {
  let aLocalDate = new Date()
  if (aDate && aDate.length === 3) {
    aLocalDate = new Date(aDate[0], aDate[1] - 1, aDate[2], 23, 59)
  }
  return aLocalDate
}

/**
 * Convert a field in "hh:mm" format into a (h)hmm integer (ex.: "01:25" will beconverted into the integer 125)
 * @param {*} fldHhmm
 */
function convertHhmmToInt(fldHhmm: string) {
  if (fldHhmm) {
    const fldParts: string[] = fldHhmm.split(':')
    if (fldParts && fldParts.length === 2) {
      return parseInt(fldParts[0]) * 100 + parseInt(fldParts[1])
    }
  }
}

/**
 * Convert a field in minutes (integer) into a "hh:mm" formatted string
 * @param {*} fldMinutes
 */
function convertMinutesToHhmm(fldMinutes: number | null | undefined) {
  if ((fldMinutes ?? -1) >= 0) {
    const minutesAsInteger = Math.floor(fldMinutes as number)
    const hours: number = Math.floor(minutesAsInteger / 60)
    const minutes: number = minutesAsInteger % 60
    return ('0' + hours.toString()).slice(-2) + ':' + ('0' + minutes.toString()).slice(-2)
  }
  console.debug('ERR: could not convert <' + fldMinutes + '> into HH:mm format')
  return null
}

/**
 * Measure the given text width when displayed in the html
 * @param {string} text The text to measure
 * @returns {number} The width of the html of the text
 */
function measureTextWidth(text: string) {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  const rootStyles = getComputedStyle(document.body)

  context.font = rootStyles.font
  const width = context?.measureText(text).width

  return width
}

/**
 * Calculate number of rows for a text area to resize to
 * @param {string} multilineString The multine string displayed in the text area
 * @param {number} textAreaWidth Width of the text area
 * @returns {number} The number of rows for the text area to resize to
 */
const getTextAreaRows = (multilineString: string, inputRef) => {
  const lines = multilineString?.split('\n')
  let rows = 2
  let sum = 0
  const textAreaWidth = inputRef.current?.getBoundingClientRect().width

  if (lines) {
    if (lines.length > 10) rows = 10
    else rows = lines.length

    if (textAreaWidth) {
      const longerLinesNumber = lines.map((line) => Math.floor(measureTextWidth(line) / textAreaWidth)).filter((l) => l)
      sum = longerLinesNumber.reduce((acc, val) => Number(acc) + Number(val), 0) || 0
    }
  }

  return rows + sum
}

function groupByKey(arr, key: string) {
  const map = new Map()

  for (const item of arr) {
    const groupValue = item[key]
    const rest = { ...item }
    delete rest[key] // on retire la clé de regroupement

    if (!map.has(groupValue)) {
      map.set(groupValue, [])
    }

    map.get(groupValue).push(rest)
  }

  // transformer en tableau
  return Array.from(map.entries()).map(([groupValue, items]) => ({
    [key]: groupValue,
    items,
  }))
}

export default {
  cleanUrl,
  dformat,
  groupByKey,
  convertArrayDateToPformat,
  convertArrayDateToLocalDate,
  convertDatePformatToArrayOfNumber,
  convertDatePformatToYyyyMmJj,
  convertDateYyyyMmJjToPformat,
  convertDatePformatToYyyySepMmSepJj,
  convertHhmmToInt,
  convertMinutesToHhmm,
  measureTextWidth,
  getTextAreaRows,
}
